import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowRight, ShieldCheck, BookText, Sparkles, GitMerge, Network,
  ScanLine, BadgeCheck, Check, Trophy, AlertTriangle, Microscope,
  Beaker, ChevronRight, Layers, FileCheck2, Library, Menu, X,
  Quote, Languages, ScrollText,
} from 'lucide-react';
import InkPhDLogo from '@/components/InkPhDLogo';
import { authStorage } from '@/utils/authStorage';
import WorkspaceMockup from '@/components/landing/WorkspaceMockup';
import ReviewerGrid from '@/components/landing/ReviewerGrid';
import PremiumDemoSection from '@/components/landing/PremiumDemoSection';
import SeoHead from '@/components/SeoHead';
import ReviewsSection from '@/components/ReviewsSection';

// =============================================================================
// Reusable animations
// =============================================================================
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

// =============================================================================
// NAVBAR
// =============================================================================
function NavBar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const signedIn = authStorage.isAuthorAuthenticated();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#workspace', label: 'Workspace' },
    { href: '#anti-halu', label: 'Evidence Engine' },
    { href: '#defense', label: 'Defense Prep' },
    { href: '#statistics', label: 'Statistics' },
    { href: '#compare', label: 'Compare' },
    { href: '#pricing', label: 'Pricing' },
    { href: '/about', label: 'About', route: true },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all border-b ${
      scrolled ? 'bg-white/85 backdrop-blur-xl border-slate-200' : 'bg-transparent border-transparent'
    }`}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo-home">
          <InkPhDLogo className="h-7" />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (l.route ? (
            <Link key={l.href} to={l.href}
              className="text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
              data-testid="landing-nav-about">
              {l.label}
            </Link>
          ) : (
            <a key={l.href} href={l.href}
              className="text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors">
              {l.label}
            </a>
          )))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {signedIn ? (
            <Link to="/thesis"
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white text-[13px] font-medium px-3.5 py-2 rounded-md transition-colors"
              data-testid="landing-nav-workspace">
              Back to my workspace <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <>
              <Link to="/thesis/login"
                className="text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
                data-testid="landing-nav-login">
                Sign in
              </Link>
              <Link to="/thesis/login"
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white text-[13px] font-medium px-3.5 py-2 rounded-md transition-colors"
                data-testid="landing-nav-cta">
                Open workspace <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 space-y-3">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="block text-sm font-medium text-slate-700">
                {l.label}
              </a>
            ))}
            <Link to={signedIn ? '/thesis' : '/thesis/login'} className="block text-sm font-semibold text-slate-900 mt-3 pt-3 border-t border-slate-100"
              data-testid="landing-mobile-cta">
              {signedIn ? 'Back to my workspace →' : 'Sign in →'}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

// =============================================================================
// HERO
// =============================================================================
function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Background grid */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
      {/* Soft tint blob */}
      <div aria-hidden className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full opacity-50"
        style={{ background: 'radial-gradient(circle, hsl(212, 90%, 96%) 0%, transparent 70%)' }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 pt-12 pb-20 lg:pt-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        {/* LEFT — copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-200 bg-white/60 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(212,90%,55%)] animate-pulse" />
            <span className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-slate-600">
              DISSERTATIONS · RESEARCH ARTICLES · SYSTEMATIC REVIEWS · GRANT PROPOSALS
            </span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="mt-5 font-serif text-[44px] sm:text-[56px] lg:text-[64px] leading-[1.02] tracking-tight text-slate-900">
            From <span className="italic text-[hsl(212,90%,38%)]">first draft</span> to{' '}
            <span className="relative inline-block">
              publication-ready
              <span className="absolute -bottom-1.5 left-0 right-0 h-[6px] bg-[hsl(212,90%,55%)]/25 -z-10" />
            </span>.
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="mt-6 text-[16.5px] leading-relaxed text-slate-600 max-w-[540px]">
            InkPhD is the scientific writing OS for medical research — dissertations,
            research articles, systematic reviews, clinical protocols, grant proposals, and more.
            Evidence-grounded text with strict <span className="font-mono text-slate-800">[1] [2]</span> citations,
            EQUATOR compliance, and a Pre-publish Readiness Score that tells you whether your manuscript would
            <span className="font-medium text-slate-800"> survive peer review or a defense</span> — before you submit.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/thesis/login"
              data-testid="landing-hero-cta"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white font-medium px-5 py-3 rounded-md transition-colors text-[14px]">
              Start writing your manuscript
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#workspace"
              data-testid="landing-hero-secondary"
              className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 font-medium px-5 py-3 text-[14px] border border-slate-200 hover:border-slate-300 rounded-md">
              See the workspace
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            className="mt-10 grid grid-cols-3 gap-5 max-w-[460px]">
            <Stat n="11" l="EQUATOR guidelines" />
            <Stat n="0" l="Hallucinated cites" />
            <Stat n="5" l="AI defense reviewers" />
          </motion.div>
        </div>

        {/* RIGHT — workspace mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative">
          <WorkspaceMockup />
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-3xl text-slate-900 leading-none mb-1">{n}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wider font-mono">{l}</div>
    </div>
  );
}

// =============================================================================
// LOGO STRIP / TRUSTED-BY (placeholder lines)
// =============================================================================
function LogoStrip() {
  const inst = [
    'Harvard Medical', 'Karolinska', 'Université Paris',
    'Cochrane', 'NEJM AI', 'EQUATOR Network',
  ];
  return (
    <section className="border-y border-slate-100 bg-white/40">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-6">
        <span className="text-[10.5px] font-mono uppercase tracking-[0.2em] text-slate-400">
          BUILT FOR THE STANDARDS OF
        </span>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          {inst.map((s) => (
            <span key={s} className="text-[12.5px] font-serif italic text-slate-500">{s}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// ANTI-HALLUCINATION DEMO
// =============================================================================
function AntiHallucinationDemo() {
  return (
    <section id="anti-halu" className="relative py-14 lg:py-20">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="EVIDENCE ENGINE"
          title={<>The same prompt, two universes apart.</>}
          desc="Plain LLMs invent confident citations to studies that don't exist. InkPhD only cites papers in your own evidence library, with verifiable DOIs and PMIDs."
        />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Plain LLM */}
          <motion.div
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                Generic LLM · No grounding
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-red-50 text-red-600 border border-red-200">
                <AlertTriangle className="h-3 w-3" /> Risk
              </span>
            </div>
            <p className="text-[14px] leading-relaxed text-slate-600">
              "Open necrosectomy carries a mortality rate of approximately 36 %
              <Frag bad>(Smith et al., 2019)</Frag>, whereas the minimally invasive
              approach reduces mortality to 12 % <Frag bad>(Johnson 2021)</Frag>.
              A recent meta-analysis confirms this finding
              <Frag bad>(Chen et al., 2023; n = 4,210)</Frag>."
            </p>
            <ul className="mt-5 space-y-1.5 text-[12px] text-red-700">
              <li className="flex items-start gap-1.5"><X className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Smith et al. 2019 — <span className="font-mono">paper does not exist</span></li>
              <li className="flex items-start gap-1.5"><X className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Chen 2023 (n = 4,210) — <span className="font-mono">fabricated sample size</span></li>
              <li className="flex items-start gap-1.5"><X className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                95 % CI and p-value omitted</li>
            </ul>
          </motion.div>

          {/* InkPhD */}
          <motion.div
            initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            className="relative border border-[hsl(212,90%,55%)]/40 bg-[hsl(212,90%,55%)]/[0.03] p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] font-mono uppercase tracking-wider text-[hsl(212,90%,38%)]">
                InkPhD · Evidence-grounded
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="h-3 w-3" /> Verified
              </span>
            </div>
            <p className="text-[14px] leading-relaxed text-slate-700">
              "Open necrosectomy was historically associated with mortality between
              23 % and 38 % across high-volume centers
              <Cite n={1} />. The step-up minimally invasive approach reduced new-onset
              multi-organ failure by 21 % <span className="font-mono text-[11.5px] text-slate-500">(95 % CI 8–34, p = 0.002)</span>
              <Cite n={2} />, though 90-day mortality did not differ significantly
              <Cite n={3} />."
            </p>
            <ul className="mt-5 space-y-1.5 text-[12px] text-emerald-700">
              <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Every <span className="font-mono">[n]</span> resolves to a real DOI in your library</li>
              <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Effect sizes copied verbatim with 95 % CI</li>
              <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                Cross-checked against Crossref + NCBI</li>
            </ul>
          </motion.div>
        </div>

        <p className="mt-10 text-center text-[12px] text-slate-500 max-w-[640px] mx-auto leading-relaxed">
          InkPhD pre-loads your dissertation's evidence pack into per-chapter
          ChromaDB collections, then forces the language model to cite ONLY from
          that pack — using the exact Manubot tokens provided by the retrieval layer.
        </p>
      </div>
    </section>
  );
}

function Frag({ children, bad }) {
  return (
    <span className={`inline-block mx-0.5 px-1 py-0 text-[12px] font-mono rounded ${
      bad ? 'bg-red-50 text-red-700 border border-red-200 line-through decoration-red-400 decoration-1' : ''
    }`}>
      {children}
    </span>
  );
}
function Cite({ n }) {
  return (
    <a href="#" className="inline-flex mx-0.5 px-1 py-0 text-[11px] font-mono font-semibold rounded
      bg-[hsl(212,90%,55%)]/15 text-[hsl(212,90%,28%)] hover:bg-[hsl(212,90%,55%)]/30 transition-colors no-underline">
      [{n}]
    </a>
  );
}

// =============================================================================
// WORKSPACE TOUR
// =============================================================================
const panes = [
  {
    title: 'Section Hierarchy',
    eyebrow: 'LEFT PANE',
    icon: Layers,
    body: 'Drag-and-drop reorder up to 3 levels of subsections. Each section owns its own evidence library — strict retrieval isolation prevents leakage across topics.',
    bullet: ['Sub-section nesting up to depth 2', 'Library badge per section', 'AI Structure Builder (auto-proposes structure in 8s)'],
  },
  {
    title: 'Scientific Editor',
    eyebrow: 'CENTER PANE',
    icon: BookText,
    body: 'Vancouver-style numbered citations are rendered live. Tab to accept ghost-text continuations. Continue button finishes the current paragraph without breaking structure.',
    bullet: ['Vancouver / APA 7 / AMA 11 / BibTeX export', 'Live ghost-text Jenni-style editor', 'Selection toolbar: expand · shorten · academic tone'],
  },
  {
    title: 'Intelligence Panel',
    eyebrow: 'RIGHT PANE',
    icon: Network,
    body: 'Scientific Copilot, Inline Evidence Inspector, Citation Verifier, EQUATOR snapshot — five live tabs running in parallel against the section you are writing.',
    bullet: ['AI Copilot · grounded in your library', 'Verifier · Crossref + NCBI cross-check', 'Reviewer · severity-scored issues with auto-fix'],
  },
];

function WorkspaceTour() {
  return (
    <section id="workspace" className="py-14 lg:py-20 bg-slate-50/50 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="THE WORKSPACE"
          title="Document-centered. Three panes. Zero context-switching."
          desc="Hierarchy, editor, intelligence — laid out as a research instrument, not a chat window."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {panes.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.55 }}
              className="border border-slate-200 bg-white p-6 hover:border-slate-300 transition-colors"
              data-testid={`workspace-tour-${i}`}>
              <div className="flex items-center justify-between mb-5">
                <p.icon className="h-5 w-5 text-slate-700" strokeWidth={1.5} />
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-400">
                  {p.eyebrow}
                </span>
              </div>
              <h3 className="font-serif text-[20px] text-slate-900 leading-tight mb-2">{p.title}</h3>
              <p className="text-[13.5px] text-slate-600 leading-relaxed mb-4">{p.body}</p>
              <ul className="space-y-1 pt-3 border-t border-slate-100">
                {p.bullet.map((b) => (
                  <li key={b} className="flex items-start gap-1.5 text-[11.5px] text-slate-600">
                    <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-[hsl(212,90%,55%)]" />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// PROJECT TYPES (NEW)
// =============================================================================
const projectTypes = [
  { name: 'Doctoral Dissertation', emoji: 'PhD', desc: 'Multi-chapter thesis with composite EQUATOR guideline · sub-section nesting · defense simulation.' },
  { name: 'Research Article', emoji: 'RA', desc: 'IMRAD structure for original research · target a specific journal · abstract + figures.' },
  { name: 'Systematic Review', emoji: 'SR', desc: 'PRISMA-compliant · auto-extracted PICO tables · risk-of-bias scaffolding.' },
  { name: 'Meta-analysis', emoji: 'MA', desc: 'Forest-plot ready · effect-size aggregation · heterogeneity reporting.' },
  { name: 'Clinical Protocol', emoji: 'CP', desc: 'SPIRIT-compliant trial protocols · ethics statement · CONSORT-ready outline.' },
  { name: 'Grant Proposal', emoji: 'GR', desc: 'Specific aims · innovation · approach · with budget-justification scaffolding.' },
  { name: 'Case Report', emoji: 'CR', desc: 'CARE-compliant single-patient narrative · timeline · informed-consent reminder.' },
  { name: 'Conference Abstract', emoji: 'CA', desc: 'Structured 250–300 word abstract for ESMO / ASCO / NEJM AI etc.' },
  { name: 'Book / Monograph', emoji: 'BK', desc: 'Chapter-based long-form · same hierarchy + section libraries as dissertation.' },
  { name: 'Editorial / Commentary', emoji: 'ED', desc: 'Short opinion piece grounded in your evidence library, with hedged scholarly voice.' },
];

function ProjectTypesSection() {
  return (
    <section className="py-14 lg:py-20">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="ONE WORKSPACE · TEN PROJECT TYPES"
          title="Whatever you're writing, the engines adapt."
          desc="Pick your project type at creation and the entire workspace reconfigures — guideline catalogue, structure templates, prompt persona, and readiness criteria all shift accordingly."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {projectTypes.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: (i % 5) * 0.06, duration: 0.45 }}
              className="border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors"
              data-testid={`project-type-${i}`}>
              <div className="flex items-center justify-between mb-2.5">
                <span className="font-mono text-[10px] font-bold tracking-wider text-[hsl(212,90%,38%)] bg-[hsl(212,90%,55%)]/10 px-1.5 py-0.5 rounded">
                  {p.emoji}
                </span>
              </div>
              <h4 className="font-serif text-[14.5px] text-slate-900 leading-tight mb-1.5">{p.name}</h4>
              <p className="text-[11.5px] text-slate-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// PREMIUM ENGINES (BENTO)
// =============================================================================
function PremiumEngines() {
  return (
    <section className="py-14 lg:py-20">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="PREMIUM ENGINES"
          title="Six independent analyzers. One readiness score."
          desc="Every analyzer runs in parallel against your manuscript and reports back into a single weighted Pre-publish Readiness audit."
        />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-3 auto-rows-[180px]">
          {/* Big — Readiness */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.55 }}
            className="md:col-span-4 md:row-span-2 border border-slate-200 bg-gradient-to-br from-amber-50/40 to-[hsl(212,90%,55%)]/[0.04] p-7 relative overflow-hidden"
            data-testid="engine-readiness">
            <div className="flex items-start justify-between">
              <div className="max-w-[60%]">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-700">
                  THE MOAT · PRE-PUBLISH READINESS
                </span>
                <h3 className="mt-3 font-serif text-[28px] leading-tight text-slate-900">
                  Will my manuscript survive peer review?
                </h3>
                <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">
                  A single 0-100 audit aggregating Citation Verifier · EQUATOR · Statistical Reporting Checker ·
                  Research Gap Detector · Novelty · Library Coverage · Length. Action items are deduped and priority-sorted.
                </p>
                <div className="mt-5 flex items-center gap-4">
                  <BreakdownPill score={92} label="Citations" />
                  <BreakdownPill score={78} label="EQUATOR" />
                  <BreakdownPill score={64} label="Stats" />
                </div>
              </div>
              <div className="hidden md:flex flex-col items-end gap-3 scale-90 origin-top-right">
                <Trophy className="h-9 w-9 text-amber-400 shrink-0" strokeWidth={1.5} />
                <ScoreRingMini score={87} />
              </div>
            </div>
            <div className="absolute bottom-6 left-7 right-7 grid grid-cols-3 gap-2 text-[10.5px] font-mono uppercase tracking-wider text-slate-400">
              <div className="border-t border-slate-200 pt-2">runs in 30-60 s</div>
              <div className="border-t border-slate-200 pt-2">4 LLMs in parallel</div>
              <div className="border-t border-slate-200 pt-2">persisted per chapter</div>
            </div>
          </motion.div>

          {/* EQUATOR */}
          <Bento
            i={1} testid="engine-equator"
            icon={ShieldCheck} eyebrow="COMPLIANCE"
            title="11 EQUATOR guidelines · 426 checklist items"
            body="CONSORT, STROBE, PRISMA, TRIPOD+AI, STARD, CARE, SPIRIT, CHEERS, ARRIVE, CONSORT-AI, CLAIM — plus a custom 42-point DISSERTATION composite curated from ICMJE + Vancouver."
          />
          <Bento
            i={2} testid="engine-defense"
            icon={Microscope} eyebrow="DEFENSE PREP"
            title="5 AI reviewers · simulated panel"
            body="Clinician · Statistician · Methodology · Journal · Committee — each returns headline concerns, unsupported claims, and suggested defense responses."
          />
          <Bento
            i={3} testid="engine-grounding"
            icon={GitMerge} eyebrow="PAPERQA2-STYLE"
            title="Claim Grounding · per-claim verdicts"
            body="Each atomic claim retrieved, judged supported / partial / unsupported. Unsupported claims get a one-click 'start refinement' suggestion."
          />
          <Bento
            i={4} testid="engine-waved"
            icon={Beaker} eyebrow="WAVE D"
            title="Stats · Gaps · Novelty"
            body="10-item statistical reporting audit, prioritized research-gap detection, and novelty scoring 0-10 per substantive claim — with verbatim quotes."
          />
        </div>
      </div>
    </section>
  );
}

function Bento({ i, testid, icon: Icon, eyebrow, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
      className="md:col-span-2 border border-slate-200 bg-white p-5 flex flex-col"
      data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="h-4.5 w-4.5 text-slate-700" strokeWidth={1.5} />
        <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-400">{eyebrow}</span>
      </div>
      <h4 className="font-serif text-[16.5px] text-slate-900 leading-tight mb-1.5">{title}</h4>
      <p className="text-[11.5px] text-slate-600 leading-relaxed mt-auto">{body}</p>
    </motion.div>
  );
}

function ScoreRingMini({ score = 87 }) {
  const r = 44, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110">
      <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth="9" />
      <circle cx="55" cy="55" r={r} fill="none" stroke="#10b981" strokeWidth="9" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 55 55)" />
      <text x="55" y="60" textAnchor="middle" fontFamily="JetBrains Mono, monospace"
        fontWeight="700" fontSize="22" fill="#0f172a">{score}</text>
    </svg>
  );
}

function BreakdownPill({ score, label }) {
  const tone = score >= 80 ? 'text-emerald-700 border-emerald-300 bg-emerald-50'
    : score >= 60 ? 'text-amber-700 border-amber-300 bg-amber-50'
    : 'text-red-700 border-red-300 bg-red-50';
  return (
    <span className={`text-[10.5px] font-mono px-1.5 py-0.5 border ${tone}`}>
      <span className="font-bold">{score}</span><span className="opacity-50">/100</span> {label}
    </span>
  );
}

// =============================================================================
// DEFENSE PANEL
// =============================================================================
function DefenseSection() {
  return (
    <section id="defense" className="py-14 lg:py-20 bg-slate-50/50 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="DEFENSE READINESS"
          title="The five questions your committee will ask. Before they ask them."
          desc="Run a parallel simulation against five distinct reviewer personas — each trained to surface the gap your draft is hoping you won't notice."
        />
        <div className="mt-12">
          <ReviewerGrid />
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// HOW IT WORKS
// =============================================================================
const steps = [
  { n: '01', title: 'Build the evidence pack',
    body: 'Upload PDFs or pull papers from PubMed with MeSH-filtered search. Each document is chunked, embedded with OpenAI text-embedding-3-small, and assigned to sections.' },
  { n: '02', title: 'Structure the manuscript',
    body: 'AI Structure Builder proposes sections with rationale, sub-topics, and MeSH-ready PubMed search terms — adapted to your project type (dissertation, research article, systematic review, grant proposal, and more).' },
  { n: '03', title: 'Write with the engines on',
    body: 'Sections generate under your selected EQUATOR guideline with strict Vancouver citations. Continue, ghost-text, selection toolbar — all evidence-grounded.' },
  { n: '04', title: 'Run the readiness audit',
    body: 'One click runs Citation Verifier + EQUATOR + Stats + Gaps + Novelty + Reviewer simulation. The score and action items tell you exactly what to fix.' },
];

function HowItWorks() {
  return (
    <section className="py-14 lg:py-20">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="HOW IT WORKS"
          title="Four steps. From empty workspace to defense-ready manuscript."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 lg:gap-px bg-slate-200">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
              className="bg-white p-7 relative">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-mono text-[11px] tracking-widest text-[hsl(212,90%,38%)]">{s.n}</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <h3 className="font-serif text-[19px] text-slate-900 leading-tight mb-3">{s.title}</h3>
              <p className="text-[12.5px] text-slate-600 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// PRICING
// =============================================================================
const tiers = [
  {
    key: 'hobby', name: 'Starter', price: '€0', sub: 'forever',
    blurb: 'Test the waters with the writing & retrieval core.',
    features: ['5 generations / month', '1 project · 10 sources', 'PubMed search + retrieval', 'Vancouver / APA / AMA export',
      'Plain LLM editor', 'Community support'],
    cta: 'Get started free', highlight: false, testid: 'landing-pricing-hobby',
  },
  {
    key: 'student', name: 'Student', price: '€12', sub: '/ month',
    yearly: 'or €120 / year — save €24 (2 months free)',
    blurb: 'For one thesis or paper, on a student budget.',
    features: ['40 generations / month', '1 project · 150 sources',
      'Global search: OpenAlex · Semantic Scholar · Crossref · arXiv',
      'Zotero library sync', 'Statistics: Kaplan-Meier · Cox · ROC · regressions',
      'Automatic figure & table numbering', 'DOCX & PDF export'],
    cta: 'Start writing', highlight: false, testid: 'landing-pricing-student',
  },
  {
    key: 'researcher', name: 'Researcher', price: '€39', sub: '/ month',
    yearly: 'or €390 / year — save €78 (2 months free)',
    blurb: 'For a single paper or review, from sources to submission.',
    features: ['150 generations / month', '5 projects · 300 sources',
      'InkPhD Deep Writer — 5 premium sections / month',
      '1 Python project with code provenance',
      'Readiness score & claim grounding', 'Statistical reporting checker',
      'DOCX · PDF · LaTeX export'],
    cta: 'Start writing', highlight: false, testid: 'landing-pricing-researcher',
  },
  {
    key: 'doctoral', name: 'Doctoral', price: '€89', sub: '/ month',
    yearly: 'or €890 / year — save €178 (2 months free)',
    blurb: 'For PhD candidates and researchers writing one defensible manuscript.',
    features: ['600 generations / month', 'Unlimited projects · 2 000 sources', '5 Python projects with code provenance', 'Unlimited InkPhD Deep Writer runs', 'All Premium Engines (Reviewer Panel, Wave D, Grounding)',
      'Pre-publish Readiness Score', 'EQUATOR compliance + auto-fix',
      'PRISMA screening + flow diagram', 'LaTeX export & 10 000+ journal styles',
      'Selection toolbar + Ghost-text editor', 'All 10 project types supported',
      'Priority email support'],
    cta: 'Start 14-day trial', highlight: true, testid: 'landing-pricing-doctoral',
  },
  {
    key: 'lab', name: 'Research Lab', price: '€279', sub: '/ month · 5 seats',
    blurb: 'For investigator teams, methodologists, and Cochrane reviewers.',
    features: ['1,500 generations / seat / month', 'Everything in Doctoral',
      'Shared project workspaces', 'Mentor share-links (signed URLs)',
      'Institutional invoicing', 'Dedicated onboarding'],
    cta: 'Contact sales', highlight: false, testid: 'landing-pricing-lab',
  },
];

const PLAN_META = {
  free: { cta: 'Get started free', to: '/thesis/register' },
  student: { cta: 'Start writing', to: '/thesis/billing' },
  researcher: { cta: 'Start writing', to: '/thesis/billing' },
  doctoral: { cta: 'Start writing', to: '/thesis/billing' },
  lab: { cta: 'Contact sales', to: '/thesis/billing' },
};

function Pricing() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/billing/plans`)
      .then(({ data }) => setLive(data))
      .catch(() => setLive(null));       // fall back to the static ladder
  }, []);

  const shown = live?.plans?.length
    ? live.plans.map((p) => ({
      key: p.key,
      name: p.name,
      price: `€${Math.round(parseFloat(p.monthly))}`,
      sub: p.key === 'free' ? 'forever' : '/ month',
      yearly: p.key === 'free' ? null
        : `or €${Math.round(parseFloat(p.yearly))} / year — save €${Math.round(parseFloat(p.monthly) * 12 - parseFloat(p.yearly))} (2 months free)`,
      blurb: p.tagline,
      features: p.features || [],
      highlight: p.badge === 'Most popular',
      cta: PLAN_META[p.key]?.cta || 'Choose plan',
      to: PLAN_META[p.key]?.to || '/thesis/billing',
      testid: `landing-pricing-${p.key}`,
    }))
    : tiers.map((t) => ({ ...t, to: t.key === 'hobby' ? '/thesis/register' : '/thesis/billing' }));

  return (
    <section id="pricing" className="py-14 lg:py-20 bg-slate-50/50 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="PRICING"
          title="Honest pricing. Every tier ships with the same anti-hallucination guarantee."
          desc="Annual saves you two months. Prices load straight from our billing system, so this page and checkout can never disagree. VAT and invoice details are on the Billing & Invoices page."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-0 md:gap-px md:bg-slate-200 md:border md:border-slate-200">
          {shown.map((t, i) => (
            <motion.div
              key={t.key}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
              data-testid={t.testid}
              className={`relative p-7 ${
                t.highlight ? 'bg-slate-900 text-white xl:scale-[1.04] xl:z-10' : 'bg-white'
              }`}>
              {t.highlight && (
                <div className="absolute -top-3 left-7 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em] bg-[hsl(212,90%,55%)] text-white">
                  RECOMMENDED
                </div>
              )}
              <h3 className={`font-serif text-[22px] mb-1 ${t.highlight ? 'text-white' : 'text-slate-900'}`}>{t.name}</h3>
              <p className={`text-[12px] mb-5 ${t.highlight ? 'text-slate-300' : 'text-slate-500'}`}>{t.blurb}</p>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className={`font-serif text-[42px] leading-none ${t.highlight ? 'text-white' : 'text-slate-900'}`}
                  data-testid={`landing-price-${t.key}`}>{t.price}</span>
                <span className={`text-[12px] ${t.highlight ? 'text-slate-300' : 'text-slate-500'}`}>{t.sub}</span>
              </div>
              {t.yearly && (
                <p className={`text-[11px] mb-4 ${t.highlight ? 'text-slate-300' : 'text-slate-500'}`}>{t.yearly}</p>
              )}
              <Link to={t.to} className={`mt-5 mb-6 block text-center py-2.5 text-[13px] font-medium transition-colors ${
                t.highlight ? 'bg-[hsl(212,90%,55%)] hover:bg-[hsl(212,90%,45%)] text-white'
                : 'bg-slate-900 hover:bg-slate-800 text-white'
              }`}>
                {t.cta}
              </Link>
              <ul className="space-y-2 text-[12.5px]">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${t.highlight ? 'text-[hsl(212,90%,75%)]' : 'text-[hsl(212,90%,38%)]'}`} />
                    <span className={t.highlight ? 'text-slate-100' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <p className="mt-5 text-[12px] text-slate-500">
          Payments handled by Mollie (cards, iDEAL, Bancontact, SEPA, Apple Pay). VAT applied under
          EU OSS rules; reverse-charge invoices available for businesses with a VAT number.{' '}
          <Link to="/legal/billing" className="underline hover:text-slate-800"
            data-testid="landing-billing-terms-link">Billing, invoices &amp; right of withdrawal</Link>
        </p>
      </div>
    </section>
  );
}


// =============================================================================
// STATISTICS & DATA
// =============================================================================
const statsFeatures = [
  {
    title: 'Survival analysis',
    body: 'Kaplan-Meier curves with the classic number-at-risk table beneath the plot, median survival, log-rank comparison and survival at your chosen horizons.',
    mono: 'KAPLAN-MEIER · LOG-RANK',
  },
  {
    title: 'Cox models you can defend',
    body: 'Hazard ratios with 95% CI plus an automatic proportional-hazards check (Schoenfeld residuals, per-covariate and global p-values) and a diagnostic log-log plot.',
    mono: 'COX HR · SCHOENFELD',
  },
  {
    title: 'Diagnostic accuracy',
    body: 'ROC curves, AUC with 95% CI, Youden cut-off with sensitivity and specificity — publication-ready in colour or print black & white.',
    mono: 'ROC · AUC · YOUDEN',
  },
  {
    title: 'Regressions & correlations',
    body: 'Linear β and logistic odds ratios with confidence intervals, baseline and group-comparison tables, Pearson/Spearman matrices with significance markers.',
    mono: 'β · OR · r',
  },
  {
    title: 'Numbering that never breaks',
    body: 'Every figure and table carries a stable marker. Reorder or delete one and every in-text mention renumbers itself — in the editor, in Word (SEQ/TOC fields) and in the PDF.',
    mono: 'AUTO CROSS-REFERENCES',
  },
  {
    title: 'The Methods paragraph, written',
    body: 'InkPhD drafts the "Statistical analysis" paragraph from the analyses you actually ran and drops it straight into your Methods section.',
    mono: 'METHODS · AUTO-DRAFT',
  },
];

function StatisticsSection() {
  return (
    <section id="statistics" className="py-14 lg:py-20 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="STATISTICS & DATA"
          title="Upload your dataset. Get the analysis, the figure and the sentence that reports it."
          desc="No exporting to SPSS and pasting screenshots back. Your CSV or Excel file becomes real survival curves, model tables and the wording a reviewer expects — computed from your data, inside the manuscript."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {statsFeatures.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.45 }}
              className="bg-white p-7"
              data-testid={`landing-stats-${i}`}>
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[hsl(212,90%,38%)]">
                {f.mono}
              </span>
              <h3 className="font-serif text-[20px] mt-2 mb-2 text-slate-900">{f.title}</h3>
              <p className="text-[13px] leading-relaxed text-slate-600">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// =============================================================================
// WHAT ONLY INKPHD DOES
// =============================================================================
const onlyHere = [
  ['Statistics inside the manuscript',
   'Survival, Cox with assumption testing, ROC/AUC and regressions computed from your own dataset — not a chat about statistics.'],
  ['Defence simulation',
   'Five expert reviewer personas interrogate your draft and hand you the answers to rehearse before the committee does.'],
  ['Pre-publish Readiness Score',
   'One honest number from seven signals: citation trust, EQUATOR, statistical reporting, evidence gaps, novelty, density and length.'],
  ['EQUATOR compliance with auto-fix',
   '11 guidelines and 426 checklist items — including a DISSERTATION composite — with retrieval-grounded fixes for what is missing.'],
  ['Every citation verified',
   'Each DOI and PMID is checked against Crossref and PubMed. Unresolved references are flagged, never quietly printed.'],
  ['Word output a university accepts',
   'Real Word tables, native SEQ/TOC fields, lists of figures and tables with page numbers, title page and declaration — plus LaTeX.'],
  ['Code-to-Methods provenance',
   'Attach a Python project and get Methods text with file-and-line evidence for every technical claim.'],
  ['PRISMA screening end to end',
   'Dedupe, AI-assisted title/abstract decisions with reasons you can overrule, the flow diagram and the study-selection paragraph.'],
];

function OnlyInkPhD() {
  return (
    <section className="py-14 lg:py-20 bg-slate-50/50">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="WHY INKPHD"
          title="The part other AI writing tools leave to you."
          desc="Reference managers find papers. Editors polish language. InkPhD is built for what happens between the data and the defence."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
          {onlyHere.map(([title, body], i) => (
            <motion.div key={title}
              initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.45 }}
              className="border-l-2 border-[hsl(212,90%,55%)] pl-5"
              data-testid={`landing-only-${i}`}>
              <h3 className="font-serif text-[19px] text-slate-900 mb-1.5">{title}</h3>
              <p className="text-[13px] leading-relaxed text-slate-600">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// HONEST COMPARISON
// =============================================================================
const WRITER_TOOLS = ['InkPhD', 'Jenni', 'Paperpal', 'StudyTexter'];
const writerRows = [
  ['Price', '€0–279 / month', '$12–30 / month',
    '$12–29 / month', '€69–249 once per paper'],
  ['What it is really for', 'Writing and defending a medical or scientific manuscript on your own evidence',
    'Fast AI drafting with autocomplete', 'Language editing and submission polish for journals',
    'Getting a finished thesis draft written for you'],
  ['Disciplines covered',
    'Medicine and the wider sciences — clinical medicine, biology, pharmacy, public health, '
    + 'dentistry, nursing, engineering and social sciences: EQUATOR (CONSORT, STROBE, PRISMA, '
    + 'SPIRIT, TRIPOD…) for clinical work, IMRAD / PRISMA / grant structures for everything else',
    'general academic writing, no methodology layer',
    'general academic English, any field', 'general academic writing'],
  ['Retrieval-grounded drafting from YOUR library', 'yes', 'partial — cites from its own library',
    'no — edits the text you already have', 'partial — writes from sources it picks'],
  ['Numbered citations verified against Crossref / PubMed', 'yes',
    'partial — citations inserted, not DOI-verified',
    'partial — checks style, not existence', 'partial — 2 853 styles, no DOI verification'],
  ['Unsupported claims underlined in the editor', 'yes', 'yes — claim confidence',
    'no', 'no'],
  ['Statistics from your own dataset',
    'yes — Kaplan-Meier, Cox + PH check, ROC/AUC, regressions', 'no', 'no', 'no'],
  ['Reporting-guideline (EQUATOR) compliance', 'yes — 11 guidelines, 426 items, auto-fix',
    'no', 'partial — journal submission checks', 'no'],
  ['Defence / reviewer simulation', 'yes — 5 reviewer personas + readiness score', 'no',
    'partial — pre-submission review report', 'no'],
  ['Figure & table numbering with native Word fields', 'yes', 'no', 'no',
    'partial — formatted document'],
  ['Word / Google Docs add-in', 'no — planned', 'partial — Word add-in',
    'yes — Word, Docs, Overleaf, browser', 'no'],
  ['Export', 'DOCX · PDF · LaTeX · Markdown · HTML', 'DOCX · LaTeX · BibTeX',
    'DOCX · LaTeX', 'DOCX / PDF of the delivered thesis'],
];

const DISCOVERY_TOOLS = ['InkPhD', 'Elicit', 'Consensus', 'SciSpace', 'ResearchRabbit'];
const discoveryRows = [
  ['Price', '€0–279 / month', 'Free tier · $10–169 / month', 'Free tier · $9–12 / month',
    'Free tier · $12–20 / month', 'Free'],
  ['Disciplines covered',
    'Medical + scientific: clinical medicine, biology, pharmacy, public health, engineering, '
    + 'social sciences — with the reporting guideline that fits each design',
    'all fields', 'all fields', 'all fields', 'all fields'],
  ['Index you can search', 'PubMed + OpenAlex + Semantic Scholar + Crossref + arXiv',
    '125M+ papers — the strongest discovery engine here', '200M+ papers via Semantic Scholar',
    '280M+ papers', 'Semantic Scholar graph'],
  ['Consensus meter (how many studies agree)', 'yes — design-weighted, with the quote it judged',
    'partial — claim summaries', 'yes — that is its whole point', 'no', 'no'],
  ['Citation network map', 'yes — internal citations + co-cited hubs + suggested reads',
    'no', 'no', 'partial — connected papers', 'yes — that is its whole point'],
  ['PRISMA screening + flow diagram', 'yes — dedupe, AI screening, diagram, paragraph',
    'yes — built for large-scale screening', 'no', 'partial', 'no'],
  ['Custom data-extraction columns', 'partial — fixed PICO findings table',
    'yes — any columns you define, exportable', 'no', 'partial', 'no'],
  ['Read-along PDF reader with notes', 'no — planned', 'no', 'no',
    'yes — chat and highlight inside the PDF', 'no'],
  ['Writes a full, citation-verified manuscript', 'yes', 'no — extractions and summaries',
    'no — answers questions', 'partial — short drafts', 'no'],
  ['Your own dataset → figures & statistics', 'yes', 'no', 'no', 'no', 'no'],
  ['Open-access full text fetched automatically by DOI',
    'yes — Europe PMC, Unpaywall, OpenAlex, arXiv', 'partial', 'no', 'yes', 'no'],
];

const honest = [
  ['Where Elicit is better',
   'Its index of 125M+ papers finds work you would otherwise miss, it screens thousands of records at a time and you can define your own extraction columns with CSV export. InkPhD retrieves from the sources you add, and our screening is built for tens to hundreds of records. If discovery is your bottleneck, run Elicit alongside InkPhD.'],
  ['Where Jenni and Paperpal are better',
   'Jenni is faster and lighter for pure drafting, and Paperpal lives inside Word, Google Docs and Overleaf — the distribution we do not have yet — with strong language editing for non-native speakers. InkPhD asks more of you up front, because the output has to survive a defence.'],
  ['Where SciSpace and ResearchRabbit are better',
   'SciSpace has a genuine read-along PDF reader with highlights and per-paragraph chat; ResearchRabbit maps the literature graph better than anyone and is free. Our map covers your own library plus its co-cited classics, not the whole graph.'],
  ['Where StudyTexter is better',
   'You pay once and receive a finished draft in days, with a human editor available. It asks very little of you. InkPhD asks for your data, your sources and your judgement on every claim — deliberately slower, because the result has to be genuinely yours.'],
  ['Where InkPhD is better',
   'Nothing else here computes survival curves, Cox models with a proportional-hazards check or ROC curves from your dataset, verifies every numbered citation against Crossref and PubMed, runs EQUATOR compliance with auto-fix, turns a draft audit into a tracked writing plan, renumbers figures with native Word fields and then interrogates your manuscript like a review panel.'],
  ['What we do not do',
   'No exam revision aids, no ghost-writing service, no plagiarism check against a global corpus (ours is semantic, against your own library), and no Word add-in yet. We would rather say it here than let you discover it after paying.'],
];

function CompareCell({ value }) {
  if (value === 'yes') return <Check className="w-4 h-4 text-[hsl(160,60%,35%)]" aria-label="yes" />;
  if (value === 'no') return <span className="text-slate-300 text-lg leading-none" aria-label="no">—</span>;
  return (
    <span className={`text-[12px] leading-snug ${value.startsWith('partial') ? 'text-amber-700' : 'text-slate-700'}`}>
      {value}
    </span>
  );
}

function CompareTable({ caption, tools, rows, testid }) {
  return (
    <div className="mt-8">
      <h3 className="font-serif text-[20px] text-slate-900 mb-3">{caption}</h3>
      <div className="overflow-x-auto border border-slate-200" data-testid={testid}>
        <table className="w-full min-w-[900px] text-left border-collapse bg-white">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="bg-slate-50">
              <th scope="col" className="p-3 text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500 w-[230px]">
                Capability
              </th>
              {tools.map((t) => (
                <th scope="col" key={t}
                  className={`p-3 text-[13px] font-serif ${t === 'InkPhD' ? 'text-[hsl(212,90%,38%)]' : 'text-slate-700'}`}>
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, ...cells], i) => (
              <tr key={label} className={i % 2 ? 'bg-slate-50/60' : ''}
                data-testid={`${testid}-row-${i}`}>
                <th scope="row" className="p-3 align-top text-[12px] font-medium text-slate-900">{label}</th>
                {cells.map((c, j) => (
                  <td key={j} className={`p-3 align-top ${j === 0 ? 'bg-[hsl(212,90%,97%)]' : ''}`}>
                    <CompareCell value={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComparisonSection() {
  return (
    <section id="compare" className="py-14 lg:py-20 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="HONEST COMPARISON"
          title="InkPhD next to the AI writers and the discovery engines."
          desc="Seven tools that get mentioned in the same breath but solve different problems. Prices as published in June 2026. Where they beat us, we say so."
        />
        <CompareTable caption="1 · Against the AI writing tools"
          tools={WRITER_TOOLS} rows={writerRows} testid="landing-comparison-writers" />
        <CompareTable caption="2 · Against the literature discovery engines"
          tools={DISCOVERY_TOOLS} rows={discoveryRows} testid="landing-comparison-discovery" />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {honest.map(([title, body], i) => (
            <div key={title} className="border border-slate-200 bg-white p-6" data-testid={`landing-honest-${i}`}>
              <h3 className="font-serif text-[18px] text-slate-900 mb-2">{title}</h3>
              <p className="text-[13px] leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-slate-500">
          Compiled June 2026 from each vendor's public pricing and feature pages. Features change —
          tell us if anything here is out of date and we will correct it.
        </p>
      </div>
    </section>
  );
}

function TrustSection() {
  const points = [
    ['EU AI Act', 'Not high-risk, not a medical device. Article 50 transparency: AI involvement is always explicit, every AI section is labelled, and each claim is traceable to its source.', '/legal/ai-act'],
    ['GDPR', 'Self-service data export and permanent erasure in the app. No model training on your manuscripts. Rights answered within 30 days.', '/legal/privacy'],
    ['Article 28 DPA', 'A signed data-processing agreement for universities and hospitals, with security measures, breach timelines and audit support.', '/legal/dpa'],
    ['Subprocessors', 'Every provider that touches your data is listed with purpose, data minimisation and location. 30 days notice before changes.', '/legal/subprocessors'],
    ['Cookies', 'Session and workspace preferences only. No advertising, no analytics, no cross-site tracking.', '/legal/cookies'],
    ['Research Use Only', 'No diagnosis, no treatment recommendations, no clinical decision support. The RUO notice is rendered on every export.', '/legal/ruo'],
  ];
  return (
    <section id="trust" className="py-14 lg:py-20 bg-slate-50/50 border-y border-slate-100">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="TRUST & COMPLIANCE"
          title="Built for the paperwork your institution will actually ask for."
          desc="Ethics committees and research offices ask three questions: who processes the data, is the AI use declared, and can the author prove human oversight. Here are the answers, in writing."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200"
          data-testid="landing-trust-grid">
          {points.map(([title, body, href], i) => (
            <Link key={title} to={href} className="bg-white p-6 hover:bg-[hsl(212,90%,98%)] transition-colors"
              data-testid={`landing-trust-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-[hsl(212,90%,38%)]" />
                <h3 className="font-serif text-[18px] text-slate-900">{title}</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-slate-600">{body}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-[12px] text-[hsl(212,90%,38%)]">
                Read the statement <ChevronRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-6 text-[12px] text-slate-500">
          Operator: International Sci Ink Press Ltd · Bulgaria, EU ·
          {' '}<Link to="/contact" className="underline hover:text-slate-800">contact form</Link>
        </p>
      </div>
    </section>
  );
}

function Safeguards() {
  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-1">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">SAFEGUARDS</span>
            <h2 className="mt-3 font-serif text-[32px] leading-tight text-slate-900">
              A writing instrument. <span className="italic">Not a medical device.</span>
            </h2>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SafePoint icon={ScanLine} title="Research Use Only"
              body="InkPhD does not provide clinical decision support. RUO disclaimer rendered on every export." />
            <SafePoint icon={BadgeCheck} title="AI Transparency"
              body="Every chapter records which sections were AI-generated, evidence support score, and reviewer approvals." />
            <SafePoint icon={FileCheck2} title="Citation Verifier"
              body="Crossref + NCBI cross-check. Unverified citations are flagged in-line, not hidden." />
            <SafePoint icon={Library} title="No global model training"
              body="Your library, your retrieval. We never train a shared model on user content — only retrieval-conditioned personalisation." />
          </div>
        </div>
      </div>
    </section>
  );
}

function SafePoint({ icon: Icon, title, body }) {
  return (
    <div className="border-l border-slate-200 pl-4 py-1">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="h-4 w-4 text-[hsl(212,90%,38%)]" strokeWidth={1.5} />
        <h4 className="font-serif text-[15px] text-slate-900">{title}</h4>
      </div>
      <p className="text-[12.5px] text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

// =============================================================================
// FINAL CTA
// =============================================================================
function FinalCTA() {
  return (
    <section className="relative py-14 lg:py-20 bg-slate-900 overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
      <div aria-hidden className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-30"
        style={{ background: 'radial-gradient(circle, hsl(212, 90%, 55%) 0%, transparent 60%)' }} />
      <div className="relative max-w-[1080px] mx-auto px-6 lg:px-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="font-serif text-[44px] sm:text-[56px] leading-[1.04] text-white">
          Your manuscript deserves a <span className="italic text-[hsl(212,90%,75%)]">rigorous</span> writing partner.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.55 }}
          className="mt-6 text-[16px] text-slate-300 max-w-[640px] mx-auto leading-relaxed">
          Start with the Hobby tier — no credit card. Upgrade when you run your first Pre-publish Readiness audit and see what's missing.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ delay: 0.2 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link to="/thesis/login"
            data-testid="landing-final-cta"
            className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-[hsl(212,90%,55%)] hover:text-white font-medium px-6 py-3 text-[14px] transition-colors">
            Open the workspace
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/legal/ai-transparency"
            className="inline-flex items-center gap-2 text-slate-200 hover:text-white text-[13px] font-medium px-4 py-3 border border-white/20 hover:border-white/40">
            Read AI transparency report
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// =============================================================================
// FOOTER
// =============================================================================
function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400 border-t border-white/5">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-2">
            <InkPhDLogo className="h-7 brightness-0 invert" />
            <p className="mt-4 text-[12.5px] leading-relaxed text-slate-500 max-w-[280px]">
              Scientific writing infrastructure for medical researchers.
              Evidence-grounded, defense-ready.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-white/10 text-slate-400">
              <ShieldCheck className="h-3 w-3" /> Research Use Only · Not clinical AI
            </div>
          </div>
          <FooterCol title="Product" items={[
            ['Workspace', '#workspace'], ['Pricing', '#pricing'],
            ['User guides', '/guides'], ['Dissertation guide', '/guide/dissertation'],
          ]} />
          <FooterCol title="Legal" items={[
            ['Terms', '/legal/terms'], ['Privacy', '/legal/privacy'],
            ['Research Use Only', '/legal/ruo'], ['Cookies', '/legal/cookies'],
          ]} />
          <FooterCol title="Compliance" items={[
            ['AI Transparency', '/legal/ai-transparency'], ['EU AI Act', '/legal/ai-act'],
            ['DPA (Art. 28)', '/legal/dpa'], ['Subprocessors', '/legal/subprocessors'],
          ]} />
          <FooterCol title="Company" items={[
            ['About InkPhD', '/about'], ['For journal authors', '/journal-authors'],
            ['Contact us', '/contact'], ['Sign in', '/thesis/login'],
          ]} />
        </div>
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500 font-mono">
          <p>© {new Date().getFullYear()} International Sci Ink Press Ltd · inkphd.com</p>
          <p>Built for the rigour of medical scholarship.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <h4 className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-slate-300 mb-3">{title}</h4>
      <ul className="space-y-2">
        {items.map(([label, href]) => (
          <li key={label}>
            {href.startsWith('#') || href.startsWith('mailto:') ? (
              <a href={href} className="text-[12.5px] text-slate-500 hover:text-white transition-colors">{label}</a>
            ) : (
              <Link to={href} className="text-[12.5px] text-slate-500 hover:text-white transition-colors">{label}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// SECTION HEADER (reusable)
// =============================================================================
function SectionHeader({ eyebrow, title, desc, align = 'left' }) {
  return (
    <div className={`max-w-[760px] ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      {eyebrow && (
        <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(212,90%,38%)]">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-3 font-serif text-[34px] sm:text-[42px] leading-[1.08] tracking-tight text-slate-900">
        {title}
      </h2>
      {desc && (
        <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-[620px]">{desc}</p>
      )}
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================
/* ---------------- Languages + RAG vs general chatbots ---------------- */

const LANGUAGES = [
  ['English', 'en'], ['Bulgarian', 'bg'], ['German', 'de'], ['French', 'fr'],
  ['Spanish', 'es'], ['Italian', 'it'], ['Portuguese', 'pt'], ['Polish', 'pl'],
  ['Romanian', 'ro'], ['Greek', 'el'], ['Turkish', 'tr'], ['Russian', 'ru'],
];

function LanguagesSection() {
  return (
    <section className="py-12 lg:py-16 border-y border-slate-100 bg-white/50" data-testid="landing-languages">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_1.3fr] gap-8 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Write in your language
          </p>
          <h2 className="text-base md:text-lg font-semibold mb-2">
            Generation in 12 languages, evidence in any language
          </h2>
          <p className="text-sm text-slate-600">
            Choose the output language per project or per section. Your sources can be in any
            language — retrieval and citations work regardless, and the interface stays English
            so co-authors and supervisors always see the same thing.
          </p>
        </div>
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {LANGUAGES.map(([name, code]) => (
            <li key={code} data-testid={`landing-language-${code}`}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 bg-white">
              <span className="text-[10px] font-mono uppercase text-slate-400 w-5">{code}</span>
              <span className="text-[13px] font-medium">{name}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const RAG_ROWS = [
  ['Where the facts come from',
    'Only from papers you uploaded or imported — retrieved, ranked and quoted',
    'The model\'s training data and whatever the user pasted'],
  ['Citations',
    'Numbered Vancouver [1], [2] resolved to DOI/PMID and re-verified against your library',
    'Often plausible-looking but unverifiable; fabricated references are a known failure mode'],
  ['What happens when evidence is missing',
    'The claim is flagged "unsupported" and you are told which search would fill the gap',
    'The sentence is written anyway, in the same confident tone'],
  ['Reporting standards',
    '11 EQUATOR guidelines + a dissertation checklist, audited item by item',
    'No notion of CONSORT, STROBE, PRISMA or SPIRIT compliance'],
  ['Statistics in your text',
    'Audited: 95% CI vs p-only, multiple testing, missing data, ITT vs per-protocol',
    'No audit — numbers are reproduced as written'],
  ['Structure of a long document',
    'Section tree with per-section libraries, versions and a readiness score per section',
    'One long chat thread; structure and consistency are the user\'s problem'],
  ['Review before submission',
    'Defence Panel: clinician, statistician, methodologist, editor, committee member',
    'Generic "improve my paper" feedback'],
  ['Deep multi-pass writing',
    'InkPhD Deep Writer: multi-perspective outline → per-subsection draft → claim-by-claim verification (Researcher plan and above)',
    'One prompt, one answer — no critique or re-retrieval loop'],
  ['Your manuscript',
    'Stored in your project, never used for training, exportable and erasable on demand',
    'Depends on the provider and the plan'],
];

function RagVsChatbots() {
  return (
    <section className="py-14 lg:py-20 bg-slate-50/60 border-y border-slate-100"
      data-testid="landing-rag-vs-chatbots">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          Why not just use a general chatbot
        </p>
        <h2 className="text-base md:text-lg font-semibold mb-2">
          InkPhD (retrieval-grounded) vs ChatGPT / Gemini (general chat)
        </h2>
        <p className="text-sm text-slate-600 mb-8 max-w-3xl">
          General assistants are excellent at language. They are not built to prove that a
          sentence is supported by a specific paper — which is the one thing a reviewer, a
          supervisor and an editor will test.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-[13px] min-w-[640px]">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 font-semibold w-1/4">&nbsp;</th>
                <th className="px-4 py-3 font-semibold text-[#1d4ed8]">InkPhD · RAG platform</th>
                <th className="px-4 py-3 font-semibold text-slate-500">ChatGPT / Gemini · general chat</th>
              </tr>
            </thead>
            <tbody>
              {RAG_ROWS.map(([label, ours, theirs]) => (
                <tr key={label} className="border-t border-slate-100 align-top">
                  <th className="px-4 py-3 text-left font-medium text-slate-700">{label}</th>
                  <td className="px-4 py-3">{ours}</td>
                  <td className="px-4 py-3 text-slate-500">{theirs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[12px] text-slate-500 mt-3">
          InkPhD uses the same frontier models underneath — the difference is what they are allowed
          to say, and what has to be proven before it reaches your manuscript.
        </p>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[hsl(210,24%,99%)] text-slate-900 antialiased" data-testid="landing-page">
      <SeoHead
        title="InkPhD — From first draft to publication-ready"
        description="Evidence-grounded scientific writing for medical research: retrieval-grounded drafting, numbered citations verified against Crossref and PubMed, EQUATOR compliance, statistics from your own dataset, PRISMA screening and defence simulation."
        path="/" />
      <NavBar />
      <Hero />
      <LogoStrip />
      <AntiHallucinationDemo />
      <WorkspaceTour />
      <ProjectTypesSection />
      <StatisticsSection />
      <PremiumEngines />
      <PremiumDemoSection />
      <DefenseSection />
      <OnlyInkPhD />
      <RagVsChatbots />
      <ComparisonSection />
      <LanguagesSection />
      <HowItWorks />
      <Pricing />
      <ReviewsSection />
      <TrustSection />
      <Safeguards />
      <FinalCTA />
      <Footer />
    </div>
  );
}
